<template>
  <water-balance-reports-main-dialog
    :is-visible.sync="computedIsVisible"
    :is-fetched="isFetched"
    :is-chart-ready="isChartReady"
    :dialog-title="titleItem"
    :table-data="tableData"
    ref="waterBalanceMainDialog"
  >
    <template #chart>
      <v-row no-gutters class="pt-10 px-15" style="gap: 2rem">
        <v-col cols="3">
          <data-input v-bind="parameterInputProps" v-model="parameter" @change="onParamChange" />
        </v-col>
        <v-col cols="3">
          <data-input v-bind="dateInput" v-model="dates" @saveDate="onDateChange" />
        </v-col>
        <v-col cols="3">
          <data-input v-bind="zonesInput" v-model="zones" @change="onZonesChange" />
        </v-col>
      </v-row>
      <v-row no-gutters class="justify-center align-center fill-height">
        <v-col v-if="isFetching" class="pt-5">
          <main-loader />
        </v-col>
        <template v-else>
          <v-col cols="10">
            <canvas id="waterbalance-chart-details" role="img" height="500px"></canvas>
          </v-col>
        </template>
      </v-row>
    </template>
    <template #prepend-table>
      <v-row v-if="parameter && parametersList[parameter]" no-gutters>
        <v-col class="text-h6 text-left px-5 pt-6">
          {{ parametersList[parameter].name }}
        </v-col>
      </v-row>
    </template>
  </water-balance-reports-main-dialog>
</template>

<script>
import { call, get } from 'vuex-pathify';

import DataInput from '@/components/DataInput';
import MainLoader from '@/components/MainLoader';
import WaterBalanceReportsMainDialog from './WaterBalanceReportsMainDialog.vue';

import { debounce } from 'lodash';

export default {
  name: 'WaterBalanceReportsDetailsDialog',
  components: {
    DataInput,
    MainLoader,
    WaterBalanceReportsMainDialog,
  },
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    data: {
      type: Object,
      default: () => ({}),
    },
    parameterInputProps: {
      type: Object,
      default: () => ({}),
    },
    dateInputProps: {
      type: Object,
      default: () => ({}),
    },
    parametersList: {
      type: Object,
      default: null,
    },
    currentReport: {
      type: Object,
      default: () => ({}),
    },
  },
  data: () => ({
    parameter: null,
    dates: null,
    zones: [],
    featuresArray: [],
    isFetched: false,
    isChartReady: false,
    mappedValues: null,
    tableItems: null,
    tableHeaders: null,
    isFetching: false,
  }),
  watch: {
    isVisible: {
      handler(nV) {
        if (!nV) return;
        if (this.isChartReady) this.$refs['waterBalanceMainDialog'].chart.destroy();
        this.dates = this.parameter = this.mappedValues = this.isChartReady = this.isFetched = null;
        this.tableHeaders = this.tableItems = this.zones = [];
      },
    },
  },
  computed: {
    waterBalanceResultsDatasource: get('admin/modulesMapping@water_balance.results_datasource'),
    waterBalanceReportsDatasourceMetadata: get('layers/metadata@:waterBalanceResultsDatasource'),
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      },
    },
    dateFormat() {
      return this.currentReport?.dateFormat;
    },
    zonesInput() {
      return {
        dataType: {
          name: 'select',
        },
        multiple: true,
        itemValue: 'dma_id',
        itemText: 'dma_name',
        label: this.$i18n.t('dialog.waterBalanceReports.dmaZone'),
        items: Object.values(this.data).map(item => item.itemData),
      };
    },
    dateInput() {
      return {
        ...this.dateInputProps,
        dataType: {
          name: 'daterange',
        },
        returnType: this.dateInputProps?.datePickerProps.type,
      };
    },
    titleItem() {
      return this.$i18n.t(`dialog.waterBalanceReports.titleDetails`);
    },
    tableSummary() {
      return this.tableHeaders?.map(header => {
        if (header.value === 'analysis_datetime') return `${this.$i18n.t('dialog.waterBalanceReports.tableSummary')}:`;
        if (['analysis_date'].includes(header.value)) return '';
        else {
          return parseFloat(
            this.tableItems.reduce((a, b) => {
              if (!b[header.value]) b[header.value] = 0;
              return a + b[header.value];
            }, 0)
          ).toFixed(2);
        }
      });
    },
    tableData() {
      return {
        headers: this.tableHeaders,
        items: this.tableItems,
        summary: this.tableSummary,
        fileName: this.parametersList[this.parameter]?.name,
      };
    },
  },
  methods: {
    getDataSourceFeatures: call('layers/getDataSourceFeatures'),
    onDateChange() {
      if (!!this.parameter && this.zones.length > 0) this.getData();
    },
    onZonesChange() {
      if (!!this.parameter && !!this.dates) this.getDataDebounce();
    },
    onParamChange() {
      if (!this.isFetched) {
        if (!!this.dates && this.zones.length > 0) this.getData();
        return;
      }
      this.$refs['waterBalanceMainDialog'].chart.data.datasets = this.getChartDatasets();
      this.$refs['waterBalanceMainDialog'].chart.update();
      this.setTableData();
    },
    setTableData() {
      const attributes = JSON.parse(
        JSON.stringify(this.waterBalanceReportsDatasourceMetadata?.attributes_schema.attributes)
      );
      this.tableHeaders = [
        {
          text: attributes.find(attr => attr.name === 'analysis_datetime')?.verbose_name,
          value: 'analysis_datetime',
          align: 'center',
        },
        {
          text: attributes.find(attr => attr.name === 'analysis_date')?.verbose_name,
          value: 'analysis_date',
          align: 'center',
        },
        ...Object.entries(this.mappedValues).map(([key, value]) => {
          return {
            value: key,
            text: value.label,
            align: 'center',
          };
        }),
      ];
      const reducedDateValues = this.featuresArray.reduce((acc, obj) => {
        const objProps = obj;
        let key = this.$dayjs.utc(objProps.analysis_date).format(this.dateFormat);
        if (!acc[key]) {
          acc[key] = {
            analysis_datetime: this.$dayjs(objProps.analysis_datetime).format('L, LT'),
          };
        }
        acc[key][objProps.dma_id] = objProps[this.parameter];
        return acc;
      }, {});
      this.tableItems = Object.entries(reducedDateValues).map(([key, value]) => {
        return {
          analysis_date: this.$dayjs(key).format('L'),
          ...value,
        };
      });
    },
    getChartDatasets() {
      return Object.values(this.mappedValues).map(item => {
        return {
          ...item,
          data: item.itemData.map(data => {
            return {
              x: this.$dayjs.utc(data.analysis_datetime).format(this.dateFormat),
              y: data[this.parameter],
            };
          }),
        };
      });
    },
    async getData() {
      this.isFetching = true;
      this.isFetched = false;
      try {
        const startDate = this.$dayjs(this.dates[0]).format('YYYY-MM-DD');
        const endDate = this.$dayjs(this.dates[1])
          .endOf(this.currentReport.dateType === 'date' ? 'd' : this.currentReport.dateType === 'month' ? 'M' : 'y')
          .format('YYYY-MM-DD');
        const r = await this.getDataSourceFeatures({
          dataSource: this.waterBalanceResultsDatasource,
          sorting: [
            {
              attribute_name: `${this.waterBalanceResultsDatasource}.analysis_date`,
              direction: 'asc',
            },
          ],
          filters: {
            '!AND': [
              {
                $IN: {
                  [`${this.waterBalanceResultsDatasource}.dma_id`]: this.zones,
                },
              },
              {
                $GREATER_OR_EQUAL: {
                  [`${this.waterBalanceResultsDatasource}.analysis_date`]: {
                    value: startDate,
                  },
                },
              },
              {
                $LESS_OR_EQUAL: {
                  [`${this.waterBalanceResultsDatasource}.analysis_date`]: {
                    value: endDate,
                  },
                },
              },
            ],
          },
        });
        this.featuresArray = r.features.map(ft => ft.properties);
        this.mappedValues = this.featuresArray.reduce((acc, obj) => {
          const objProps = obj;
          let key = objProps.dma_id;
          if (!acc[key]) {
            const color = this.$_getRandomColor();
            acc[key] = {
              label: objProps.dma_name,
              dma_id: objProps.dma_id,
              borderColor: color,
              borderWidth: 1,
              fill: false,
              tension: 0,
              itemData: [],
            };
          }
          acc[key].itemData.push(objProps);
          return acc;
        }, {});
        this.setTableData();
        this.isFetching = false;
        this.isFetched = true;
        this.$nextTick(() => {
          this.drawChart();
        });
      } catch (e) {
        console.error('error:', e);
      }
    },
    getXAxes() {
      return {
        id: 'bottom',
        type: 'time',
        time: {
          unit: this.dateInputProps.datePickerProps.type === 'date' ? 'day' : this.dateInputProps.datePickerProps.type,
          displayFormats: {
            day: 'YYYY-MM-DD',
            month: 'YYYY-MM',
            year: 'YYYY',
          },
        },
        bounds: 'ticks',
        position: 'bottom',
      };
    },
    drawChart() {
      if (this.isChartReady) {
        this.$refs['waterBalanceMainDialog'].chart.destroy();
      }
      this.isChartReady = false;
      const canvas = document.getElementById('waterbalance-chart-details');
      const chartOptions = {
        options: {
          tooltips: {
            callbacks: {
              labelColor: (tooltipItem, chart) => {
                return {
                  backgroundColor: chart.data.datasets[tooltipItem.datasetIndex].borderColor,
                };
              },
              title: (tooltipItems, data) => {
                return tooltipItems.map(element => `${data.datasets[element.datasetIndex].label}`);
              },
            },
          },
          maintainAspectRatio: false,
          scales: {
            yAxes: [
              {
                id: 'left',
                type: 'linear',
                position: 'left',
                scaleLabel: {
                  display: true,
                  labelString: this.parametersList[this.parameter].label,
                },
              },
            ],
            xAxes: [this.getXAxes()],
          },
        },
        type: 'line',
        data: {
          datasets: this.getChartDatasets(),
        },
      };
      this.$refs['waterBalanceMainDialog'].generateChart(canvas, chartOptions);
      this.isChartReady = true;
    },
  },
  mounted() {
    this.getDataDebounce = debounce(this.getData, 300);
  },
};
</script>

<style lang="scss" scoped></style>
